import { useState } from "react"
import { Button } from "@/shared/ui/button.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover.tsx"
import { ScrollArea } from "@/shared/ui/scroll-area.tsx"
import { cn } from "@/lib/utils.ts"

interface TimePickerProps {
  value?: string
  onChange?: (time: string) => void
  placeholder?: string
  className?: string
}

export function TimePicker({ value, onChange, placeholder = "Select time", className }: TimePickerProps) {
  const [open, setOpen] = useState(false)

  // Parse the current time value
  const parseTime = (timeString?: string) => {
    if (!timeString) return { hours: 12, minutes: 0, period: "AM" }

    const [time] = timeString.split(" ")
    const [hours24, minutes] = time.split(":").map(Number)

    let hours12 = hours24
    let period = "AM"

    if (hours24 === 0) {
      hours12 = 12
      period = "AM"
    } else if (hours24 === 12) {
      hours12 = 12
      period = "PM"
    } else if (hours24 > 12) {
      hours12 = hours24 - 12
      period = "PM"
    }

    return { hours: hours12, minutes, period }
  }

  const { hours, minutes, period } = parseTime(value)

  const formatTime = (h: number, m: number, p: string) => {
    let hours24 = h
    if (p === "PM" && h !== 12) {
      hours24 = h + 12
    } else if (p === "AM" && h === 12) {
      hours24 = 0
    }

    const formattedHours = hours24.toString().padStart(2, "0")
    const formattedMinutes = m.toString().padStart(2, "0")
    return `${formattedHours}:${formattedMinutes}:00`
  }

  const formatDisplayTime = (timeString: string) => {
    const [time] = timeString.split(" ")
    const [hours24, minutes] = time.split(":").map(Number)

    let hours12 = hours24
    let period = "AM"

    if (hours24 === 0) {
      hours12 = 12
      period = "AM"
    } else if (hours24 === 12) {
      hours12 = 12
      period = "PM"
    } else if (hours24 > 12) {
      hours12 = hours24 - 12
      period = "PM"
    }

    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`
  }

  const handleTimeChange = (newHours: number, newMinutes: number, newPeriod: string) => {
    const timeString = formatTime(newHours, newMinutes, newPeriod)
    onChange?.(timeString)
  }

  const TimeSelector = ({
    type,
    currentValue,
    onSelect,
  }: {
    type: "hours" | "minutes" | "period"
    currentValue: number | string
    onSelect: (value: number | string) => void
  }) => {
    let items: (number | string)[] = []

    if (type === "hours") {
      items = Array.from({ length: 12 }, (_, i) => i + 1)
    } else if (type === "minutes") {
      items = Array.from({ length: 60 }, (_, i) => i)
    } else {
      items = ["AM", "PM"]
    }

    return (
      <ScrollArea className="h-48">
        <div className="flex flex-col">
          {items.map((item) => (
            <Button
              key={item}
              variant={currentValue === item ? "default" : "ghost"}
              className={cn(
                "justify-center h-8 text-sm",
                currentValue === item && "bg-primary text-primary-foreground",
              )}
              onClick={() => onSelect(item)}
            >
              {type === "minutes" ? item.toString().padStart(2, "0") : item}
            </Button>
          ))}
        </div>
      </ScrollArea>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-[200px] justify-start text-left font-normal", !value && "text-muted-foreground", className)}
        >
          {value ? formatDisplayTime(value) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="flex flex-col border-r">
            <div className="p-2 text-center text-sm font-medium border-b">Hours</div>
            <TimeSelector
              type="hours"
              currentValue={hours}
              onSelect={(h) => handleTimeChange(h as number, minutes, period)}
            />
          </div>
          <div className="flex flex-col border-r">
            <div className="p-2 text-center text-sm font-medium border-b">Minutes</div>
            <TimeSelector
              type="minutes"
              currentValue={minutes}
              onSelect={(m) => handleTimeChange(hours, m as number, period)}
            />
          </div>
          <div className="flex flex-col">
            <div className="p-2 text-center text-sm font-medium border-b">Period</div>
            <TimeSelector
              type="period"
              currentValue={period}
              onSelect={(p) => handleTimeChange(hours, minutes, p as string)}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
